import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert } from "../../components/Alert";
import { DuplicateNotice } from "../../components/crm/DuplicateNotice";
import { RequirePermission } from "../../components/crm/RequirePermission";
import { Spinner } from "../../components/Spinner";
import {
  createContact,
  duplicateCandidateId,
  getContact,
  isApiError,
  listCompanies,
  updateContact,
} from "../../lib/crm-api";
import type { Company, CrmStatus } from "../../lib/crm-types";
import { useConfig } from "../../state/config-context";
import { useSession } from "../../state/session-context";

function ContactFormInner({ id }: { id: string | undefined }): ReactNode {
  const { t } = useConfig();
  const { hasPermission } = useSession();
  const canReadCompanies = hasPermission("crm.company.read");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editing = id !== undefined;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyId, setCompanyId] = useState(searchParams.get("company_id") ?? "");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [mobile, setMobile] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [status, setStatus] = useState<CrmStatus>("active");
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<{ message: string; candidate: string | null } | null>(
    null,
  );

  useEffect(() => {
    if (!canReadCompanies) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const page = await listCompanies({ limit: 100 });
        if (!cancelled) {
          setCompanies(page.items);
        }
      } catch {
        // Optional selector; ignore failures.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canReadCompanies]);

  useEffect(() => {
    if (id === undefined) {
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const contact = await getContact(id);
        if (cancelled) return;
        setFirstName(contact.first_name);
        setLastName(contact.last_name ?? "");
        setCompanyId(contact.company_id ?? "");
        setEmail(contact.email ?? "");
        setPhone(contact.phone ?? "");
        setMobile(contact.mobile ?? "");
        setJobTitle(contact.job_title ?? "");
        setStatus((contact.status as CrmStatus) ?? "active");
      } catch (caught) {
        if (!cancelled) {
          setError(isApiError(caught) ? caught.detail : t("error.network"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault();
      setSaving(true);
      setError(null);
      setDuplicate(null);
      const input = {
        first_name: firstName.trim(),
        last_name: lastName.trim() === "" ? null : lastName.trim(),
        company_id: companyId === "" ? null : companyId,
        email: email.trim() === "" ? null : email.trim().toLowerCase(),
        phone: phone.trim() === "" ? null : phone.trim(),
        mobile: mobile.trim() === "" ? null : mobile.trim(),
        job_title: jobTitle.trim() === "" ? null : jobTitle.trim(),
        status,
      };
      try {
        const result =
          id === undefined ? await createContact(input) : await updateContact(id, input);
        navigate(`/crm/contacts/${result.id}`, { replace: true });
      } catch (caught) {
        const candidate = duplicateCandidateId(caught);
        if (candidate !== null && isApiError(caught)) {
          setDuplicate({ message: caught.detail, candidate });
        } else {
          setError(isApiError(caught) ? caught.detail : t("error.network"));
        }
      } finally {
        setSaving(false);
      }
    },
    [companyId, email, firstName, id, jobTitle, lastName, mobile, navigate, phone, status, t],
  );

  const title = editing ? t("crm.contacts.edit") : t("crm.contacts.create");

  return (
    <section className="page" aria-labelledby="crm-contact-form-title">
      <header className="page__header">
        <h1 id="crm-contact-form-title">{title}</h1>
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}
      {duplicate !== null ? (
        <DuplicateNotice
          message={duplicate.message}
          candidateId={duplicate.candidate}
          basePath="/crm/contacts"
        />
      ) : null}

      {loading ? (
        <Spinner label={t("app.loading")} />
      ) : (
        <form className="form form--inline-card" onSubmit={(event) => void onSubmit(event)}>
          <div className="form-grid">
            <label className="field" htmlFor="contact-first-name">
              <span>{t("crm.contact.firstName")}</span>
              <input
                id="contact-first-name"
                required
                maxLength={120}
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
              />
            </label>
            <label className="field" htmlFor="contact-last-name">
              <span>{t("crm.contact.lastName")}</span>
              <input
                id="contact-last-name"
                maxLength={120}
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
              />
            </label>
            {canReadCompanies ? (
              <label className="field" htmlFor="contact-company">
                <span>{t("crm.contact.company")}</span>
                <select
                  id="contact-company"
                  value={companyId}
                  onChange={(event) => setCompanyId(event.target.value)}
                >
                  <option value="">{t("crm.contact.companyNone")}</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label className="field" htmlFor="contact-email">
              <span>{t("crm.contact.email")}</span>
              <input
                id="contact-email"
                type="email"
                maxLength={320}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <label className="field" htmlFor="contact-phone">
              <span>{t("crm.contact.phone")}</span>
              <input
                id="contact-phone"
                maxLength={40}
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
              />
            </label>
            <label className="field" htmlFor="contact-mobile">
              <span>{t("crm.contact.mobile")}</span>
              <input
                id="contact-mobile"
                maxLength={40}
                value={mobile}
                onChange={(event) => setMobile(event.target.value)}
              />
            </label>
            <label className="field" htmlFor="contact-job-title">
              <span>{t("crm.contact.jobTitle")}</span>
              <input
                id="contact-job-title"
                maxLength={120}
                value={jobTitle}
                onChange={(event) => setJobTitle(event.target.value)}
              />
            </label>
            <label className="field" htmlFor="contact-status">
              <span>{t("crm.common.status")}</span>
              <select
                id="contact-status"
                value={status}
                onChange={(event) => setStatus(event.target.value as CrmStatus)}
              >
                <option value="active">{t("crm.status.active")}</option>
                <option value="inactive">{t("crm.status.inactive")}</option>
                <option value="archived">{t("crm.status.archived")}</option>
              </select>
            </label>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn--ghost" onClick={() => navigate(-1)}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn btn--primary" disabled={saving}>
              {saving ? t("common.saving") : t("common.save")}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

export function ContactFormPage(): ReactNode {
  const { id } = useParams<{ id: string }>();
  return (
    <RequirePermission permission={id === undefined ? "crm.contact.create" : "crm.contact.update"}>
      <ContactFormInner id={id} />
    </RequirePermission>
  );
}
